import { useState } from 'react';
import type { ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { PrescriptionWithRelations } from '@dpfs/shared';
import { prescriptionsService } from '@/services/prescriptions';
import { getApiError } from '@/services/api';
import { SharePrescriptionModal } from './SharePrescriptionModal';
import { formatPackagingHint } from '@dpfs/shared/packaging';
import {
  AlertCircle,
  ArrowLeft,
  Ban,
  Copy,
  FileText,
  ImageIcon,
  PenLine,
  Printer,
  QrCode,
  Share2,
} from 'lucide-react';
import { DrugNoticeModal } from '@/components/DrugNoticeModal';
import { patientIdentifier } from '@/lib/patient';
import { PageHeader, StatusBadge, EmptyState, LoadingState } from '@/components/ui';

// GET /prescriptions/:id returns a richer patient object than the shared PatientSummary
// (decrypted date of birth and an email flag; see api prescriptions.service getPrescriptionById).
type DetailPatient = PrescriptionWithRelations['patient'] & {
  dateOfBirth?: string | null;
  hasEmail?: boolean;
};

function InfoItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="mt-0.5 break-words font-medium text-gray-900">{children}</dd>
    </div>
  );
}

export function PrescriptionDetail() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [noticeModalDrugId, setNoticeModalDrugId] = useState<string | null>(null);

  const { data: prescription, isLoading } = useQuery({
    queryKey: ['prescription', id],
    queryFn: () => prescriptionsService.getById(id!),
    enabled: !!id,
  });

  const signMutation = useMutation({
    mutationFn: () => prescriptionsService.sign(id!),
    onSuccess: (data) => {
      setError(null);
      setQrCode(data.qrCode);
      queryClient.invalidateQueries({ queryKey: ['prescription', id] });
    },
    onError: (err) => {
      setError(getApiError(err).message);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => prescriptionsService.cancel(id!),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['prescription', id] });
    },
    onError: (err) => {
      setError(getApiError(err).message);
    },
  });

  const refreshQRMutation = useMutation({
    mutationFn: () => prescriptionsService.getQRCode(id!),
    onSuccess: (data) => {
      setQrCode(data.qrCode);
    },
    onError: (err) => {
      setError(getApiError(err).message);
    },
  });

  const backButton = (
    <button
      type="button"
      onClick={() => navigate(-1)}
      className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 print:hidden"
    >
      <ArrowLeft className="h-4 w-4 rtl-flip" />
      {t('doctor.rx.back')}
    </button>
  );

  if (isLoading) {
    return <LoadingState />;
  }

  if (!prescription) {
    return (
      <div className="mx-auto max-w-3xl">
        {backButton}
        <div className="card p-0">
          <EmptyState icon={FileText} title={t('doctor.rx.notFound')} />
        </div>
      </div>
    );
  }

  const documentType = prescription.documentType || 'MEDICATION';
  const isMedication = documentType === 'MEDICATION';
  const patient = prescription.patient as DetailPatient | undefined;
  const patientId = patient ? patientIdentifier(patient) : null;
  const busy = signMutation.isPending || cancelMutation.isPending;

  return (
    <div className="mx-auto max-w-3xl">
      {backButton}
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span>
              {t('prescription.title')} <span className="font-mono">#{prescription.id.slice(-8)}</span>
            </span>
            <StatusBadge status={prescription.status} />
            {!isMedication && (
              <span className="badge-primary">
                {t(`prescription.docTypesShort.${documentType.toLowerCase()}`)}
              </span>
            )}
          </span>
        }
        description={
          <span className="flex flex-wrap gap-x-4 gap-y-1">
            <span>
              {t('doctor.rx.createdOn', {
                date: new Date(prescription.createdAt).toLocaleDateString(dateLocale),
              })}
            </span>
            {prescription.expiresAt && (
              <span>
                {t('doctor.expiresOn', {
                  date: new Date(prescription.expiresAt).toLocaleDateString(dateLocale),
                })}
              </span>
            )}
          </span>
        }
      />

      {error && (
        <div className="alert-danger mb-4" role="alert">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="break-words">{error}</span>
        </div>
      )}

      {/* Patient info */}
      <div className="card mb-4">
        <h2 className="card-title mb-4">{t('prescription.patient')}</h2>
        {patient ? (
          <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            <InfoItem label={t('doctor.rx.patientName')}>
              {patient.firstName} {patient.lastName}
            </InfoItem>
            <InfoItem label={t(patientId!.typeKey)}>
              <span className="font-mono">{patientId!.masked}</span>
            </InfoItem>
            <InfoItem label={t('patient.dateOfBirth')}>
              {patient.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString(dateLocale) : '—'}
            </InfoItem>
            <InfoItem label={t('patient.gender')}>
              {patient.gender ? t(`patient.${patient.gender.toLowerCase()}`) : '—'}
            </InfoItem>
          </dl>
        ) : (
          <p className="text-sm text-gray-500">—</p>
        )}
      </div>

      {/* Medications / Orders */}
      <div className="card mb-4">
        <h2 className="card-title mb-4">
          {isMedication ? t('prescription.medications') : t('prescription.orders')}
        </h2>
        <ol className="space-y-3">
          {prescription.items.map((item, index) => {
            const drug = item.drug;
            if (!drug) {
              return (
                <li key={item.id} className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <p className="break-words font-medium text-gray-900">
                    {index + 1}. {item.itemLabel}
                  </p>
                  {item.instructions && (
                    <p className="mt-1 whitespace-pre-line break-words text-sm text-gray-700">{item.instructions}</p>
                  )}
                </li>
              );
            }

            const packagingHint =
              item.quantity != null ? formatPackagingHint(item.quantity, drug.packagingSize, drug.dosageForm) : null;

            return (
              <li key={item.id} className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                      <p className="break-words text-base font-semibold text-gray-900">{drug.tradeName}</p>
                      <button
                        type="button"
                        className="btn-icon h-7 w-7 text-primary-600 hover:text-primary-700"
                        title={t('doctor.rx.viewNotice')}
                        aria-label={t('doctor.rx.viewNotice')}
                        onClick={() => setNoticeModalDrugId(drug.id)}
                      >
                        <FileText className="h-4 w-4" />
                      </button>
                      <a
                        href={`https://www.google.com/search?q=${encodeURIComponent(drug.tradeName + ' ' + drug.strength + ' boite')}&tbm=isch`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-icon h-7 w-7 text-gray-400"
                        title={t('doctor.rx.viewPackaging')}
                        aria-label={t('doctor.rx.viewPackaging')}
                      >
                        <ImageIcon className="h-4 w-4" />
                      </a>
                      {drug.controlledClass && (
                        <span className="badge-warning">{t('doctor.rx.tableau', { cls: drug.controlledClass })}</span>
                      )}
                    </div>
                    <p className="break-words text-sm text-gray-600">
                      {drug.dci} - {drug.strength} - {drug.dosageForm}
                    </p>
                  </div>
                  {item.quantity != null && (
                    <div className="shrink-0 sm:text-end">
                      <span className="badge-gray">
                        {item.quantity} {item.quantityUnit || 'x'}
                      </span>
                      {packagingHint && <p className="mt-1 text-xs text-gray-500">{packagingHint}</p>}
                    </div>
                  )}
                </div>
                <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="inline font-medium text-gray-700">{t('prescription.dosage')}: </dt>
                    <dd className="inline text-gray-900">{item.dosage}</dd>
                  </div>
                  <div>
                    <dt className="inline font-medium text-gray-700">{t('prescription.frequency')}: </dt>
                    <dd className="inline text-gray-900">{item.frequency}</dd>
                  </div>
                  {item.duration && (
                    <div>
                      <dt className="inline font-medium text-gray-700">{t('prescription.duration')}: </dt>
                      <dd className="inline text-gray-900">{item.duration}</dd>
                    </div>
                  )}
                  {item.instructions && (
                    <div className="sm:col-span-2">
                      <dt className="inline font-medium text-gray-700">{t('prescription.instructions')}: </dt>
                      <dd className="inline break-words text-gray-900">{item.instructions}</dd>
                    </div>
                  )}
                </dl>
                <p className="mt-2 text-xs text-gray-500">
                  {item.substitutionAllowed ? t('prescription.substitutionAllowed') : t('doctor.rx.notSubstitutable')}
                </p>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Additional info */}
      {(prescription.diagnosis || prescription.notes) && (
        <div className="card mb-4">
          <dl className="space-y-3">
            {prescription.diagnosis && (
              <div>
                <dt className="text-sm text-gray-500">{t('prescription.diagnosis')}</dt>
                <dd className="mt-0.5 whitespace-pre-line break-words text-gray-900">{prescription.diagnosis}</dd>
              </div>
            )}
            {prescription.notes && (
              <div>
                <dt className="text-sm text-gray-500">{t('prescription.notes')}</dt>
                <dd className="mt-0.5 whitespace-pre-line break-words text-gray-900">{prescription.notes}</dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* QR Code */}
      {(qrCode || prescription.status === 'ACTIVE') && (
        <div className="card mb-4 text-center">
          <h2 className="card-title mb-1">{t('prescription.qrCode')}</h2>
          <p className="mb-4 text-sm text-gray-500">{t('doctor.rx.qrHint')}</p>
          {qrCode ? (
            <img
              src={qrCode}
              alt={t('prescription.qrCode')}
              className="mx-auto h-56 w-56 rounded-lg border border-gray-100 sm:h-64 sm:w-64"
            />
          ) : (
            <button
              type="button"
              onClick={() => refreshQRMutation.mutate()}
              disabled={refreshQRMutation.isPending}
              className="btn-primary"
            >
              <QrCode className="h-4 w-4" />
              {refreshQRMutation.isPending ? t('common.loading') : t('doctor.rx.showQr')}
            </button>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="card flex flex-col gap-3 p-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end empty:hidden print:hidden">
        {(prescription.status === 'DRAFT' || prescription.status === 'ACTIVE') && (
          <button
            type="button"
            onClick={() => {
              if (confirm(t('doctor.rx.cancelConfirm'))) {
                cancelMutation.mutate();
              }
            }}
            disabled={busy}
            className="btn-ghost text-danger-700 hover:bg-danger-50 hover:text-danger-800 sm:me-auto"
          >
            <Ban className="h-4 w-4" />
            {cancelMutation.isPending ? t('common.loading') : t('doctor.rx.cancelPrescription')}
          </button>
        )}

        {qrCode && (
          <button type="button" onClick={() => window.print()} className="btn-secondary">
            <Printer className="h-4 w-4" />
            {t('prescription.print')}
          </button>
        )}

        {prescription.status !== 'DRAFT' && isMedication && (
          <button
            type="button"
            onClick={() => {
              const itemsParam = encodeURIComponent(JSON.stringify(
                prescription.items.map((item) => ({
                  drugId: item.drug?.id,
                  quantity: item.quantity,
                  quantityUnit: item.quantityUnit,
                  dosage: item.dosage,
                  frequency: item.frequency,
                  duration: item.duration,
                  instructions: item.instructions,
                  substitutionAllowed: item.substitutionAllowed,
                }))
              ));
              navigate(`/doctor/prescriptions/new?patientId=${prescription.patientId}&duplicate=${prescription.id}&items=${itemsParam}`);
            }}
            className="btn-secondary"
          >
            <Copy className="h-4 w-4" />
            {t('doctor.duplicate')}
          </button>
        )}

        {(prescription.status === 'ACTIVE' || prescription.status === 'PARTIALLY_FULFILLED') && (
          <button type="button" onClick={() => setShowShareModal(true)} className="btn-secondary">
            <Share2 className="h-4 w-4" />
            {t('share.share')}
          </button>
        )}

        {prescription.status === 'DRAFT' && (
          <button
            type="button"
            onClick={() => signMutation.mutate()}
            disabled={busy}
            className="btn-primary"
          >
            <PenLine className="h-4 w-4" />
            {signMutation.isPending ? t('common.loading') : t('prescription.sign')}
          </button>
        )}
      </div>

      <SharePrescriptionModal
        prescriptionId={id!}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        patientHasEmail={patient?.hasEmail ?? false}
      />

      <DrugNoticeModal
        drugId={noticeModalDrugId}
        isOpen={!!noticeModalDrugId}
        onClose={() => setNoticeModalDrugId(null)}
      />
    </div>
  );
}
